import { App } from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import { ChevronLeft, Clock } from "lucide-react";

import {
  useDeleteDataBlockMutation,
  useGetDataBlockVersionsQuery,
  useLazyGetDataBlockByIdQuery,
  useLazyGetDataTemplateByIdQuery,
  useLazyIncrementDataBlockViewsQuery,
} from "@/redux/service";
import { isPaginatedResponse } from "@/redux/typeGuards";
import { usePageBanner } from "@/shared/contexts/PageBannerContext";
import { getEvidenceTitle } from "@/domains/evidence/hooks/useEvidenceFields";
import { PAGINATION } from "@/shared/utils/constants";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseTag from "@/components_v2/Tags/BaseTag";
import { getContentItemForField } from "@/domains/datablocks/fields/renderers";
import { useDataBlockPermissions } from "@/domains/datablocks/hooks/useDataBlockPermissions";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import CancelConfirmationModal from "@/components_v2/Modal/CancelConfirmationModal";
import {
  DataBlockContent,
  DataBlockDetails,
} from "@/domains/datablocks/components/DataBlockContent";
import { OrganizationRoles } from "@/shared/utils/constants";
import EditRequestModal from "@/domains/evidence-requests/components/EditRequestModal";
import { FieldValueResponse } from "@/redux/datablocks/types";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { DataBlockRequestEditModalsState } from "@/features/evidence-requests/list/types";
import DataBlockSidebar from "@/features/datablocks/details/components/DataBlockSidebar";
import { DataBlockActionButtons } from "@/features/datablocks/details/components/DataBlockActionButtons";
import { DataBlockContainerProps } from "@/features/datablocks/details/types";

import "./styles.css";

const DataBlockContainer = ({
  initialDataBlock,
  showCtas = true,
}: DataBlockContainerProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const { dataBlockId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [getDataBlockById, { data: fetchedDataBlock }] =
    useLazyGetDataBlockByIdQuery();
  const [getDataTemplateById, { data: dataTemplate }] =
    useLazyGetDataTemplateByIdQuery();
  const currentUser = useSelector(selectCurrentUser);
  const [deleteDataBlock] = useDeleteDataBlockMutation();
  const [incrementDataBlockViews] = useLazyIncrementDataBlockViewsQuery();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const dataBlock = useMemo(
    () => initialDataBlock || fetchedDataBlock,
    [initialDataBlock, fetchedDataBlock],
  );
  const [permissionChecked, setPermissionChecked] = useState(false);
  const [editRequestModal, setEditRequestModal] =
    useState<DataBlockRequestEditModalsState>({
      open: false,
      request: undefined,
    });
  const { canView } = useDataBlockPermissions({
    dataBlock,
    dataTemplate,
    currentUser,
  });

  const { setBannerContent, clearBanner } = usePageBanner();
  const { data: versionsData } = useGetDataBlockVersionsQuery(
    {
      id: Number(dataBlockId),
      page: 1,
      pageSize: PAGINATION.PAGE_SIZE.DATA_BLOCK_VERSION,
    },
    { skip: !dataBlockId || !dataBlock || dataBlock.editable },
  );

  const originalVersionId = useMemo(() => {
    if (!versionsData) return null;
    const results = isPaginatedResponse(versionsData)
      ? versionsData.results
      : versionsData;
    const editableVersion = results.find((v) => v.editable);
    return editableVersion?.id ?? null;
  }, [versionsData]);

  const titleFieldValue = useMemo(() => {
    if (!dataTemplate || !dataBlock) return;
    const field = dataTemplate.templateFields.find(
      (tf) => tf.id === dataBlock.fieldsMapping.title,
    );
    const value = dataBlock.fieldValues.find(
      (fv: FieldValueResponse) => fv.field === dataBlock.fieldsMapping.title,
    )?.value;
    if (!field || !value) return;

    return {
      field,
      value,
    };
  }, [dataTemplate, dataBlock]);

  useEffect(() => {
    if (dataTemplate && currentUser && dataBlock) {
      if (!canView) {
        notification.error({
          message: t("datablocks.errors.unauthorized"),
        });
        navigate(`/evidence`);
      }
      setPermissionChecked(true);
    }
  }, [dataTemplate, currentUser, dataBlock]);

  useEffect(() => {
    if (dataBlockId) {
      getDataBlockById(dataBlockId);
    }
  }, [dataBlockId]);

  useEffect(() => {
    if (dataBlock) {
      getDataTemplateById(dataBlock.template.id);
      setTimeout(() => {
        incrementDataBlockViews(dataBlock.id);
      }, 10000);
    }
  }, [dataBlock]);

  useEffect(() => {
    if (
      !dataBlock ||
      dataBlock.editable ||
      !originalVersionId ||
      !versionsData
    ) {
      clearBanner();
      return;
    }

    const results = isPaginatedResponse(versionsData)
      ? versionsData.results
      : versionsData;
    const currentVersion = results.find(
      (version) => version.id === dataBlock.id,
    );
    if (!currentVersion) return;
    let title = getEvidenceTitle(dataBlock);
    const currentVersionSurvey = currentVersion.references.surveys[0];
    if (currentVersionSurvey) {
      title = currentVersionSurvey.label;
    }

    const lockedDate = dataBlock.updatedAt
      ? DateTime.fromISO(dataBlock.updatedAt).toFormat("d MMMM yyyy")
      : null;

    setBannerContent({
      title: t("datablocks.labels.viewingEvidenceVersion", { title }),
      description: lockedDate
        ? t("datablocks.labels.lockedOnDate", { date: lockedDate })
        : undefined,
      icon: <Clock color="var(--orange-700)" />,
      titleColor: "var(--orange-900)",
      descriptionColor: "var(--orange-900)",
      backgroundColor: "var(--orange-500-10)",
      buttons: [
        {
          children: t("datablocks.labels.backToCurrentVersion"),
          variant: BUTTON_VARIANTS.TERTIARY,
          size: BUTTON_SIZES.SMALL,
          leadingIcon: <ChevronLeft size={16} />,
          onClick: () =>
            navigate(`/evidence/${originalVersionId}/`, {
              state: { showBackButton: true },
            }),
          className: "data-block-version-banner-button",
        },
      ],
    });

    return () => clearBanner();
  }, [dataBlock, originalVersionId, t, setBannerContent, clearBanner]);

  const handleDelete = () => {
    deleteDataBlock(dataBlockId)
      .unwrap()
      .then(() => {
        notification.success({
          message: t("datablocks.labels.datablockIsDeleted"),
        });
        navigate("/evidence");
      });
  };

  const calculateTotalHeight = () => {
    const headerButton = document.querySelector(
      ".data-block-header-buttons",
    ) as HTMLElement;
    const headerBlock = document.querySelector(
      ".data-block-header",
    ) as HTMLElement;
    const baseHeader = document.querySelector(".base-header") as HTMLElement;
    const mainContainer = document.querySelector(
      ".main-content-container",
    ) as HTMLElement;
    const footer = document.querySelector("#footer") as HTMLElement;

    const mainContainerPadding = mainContainer
      ? window.getComputedStyle(mainContainer)
      : null;
    const paddingTop = mainContainerPadding
      ? parseFloat(mainContainerPadding.paddingTop)
      : 0;
    const paddingBottom = mainContainerPadding
      ? parseFloat(mainContainerPadding.paddingBottom)
      : 0;

    const height =
      (headerButton?.offsetHeight || 0) +
      (headerBlock?.offsetHeight || 0) +
      (baseHeader?.offsetHeight || 0) +
      paddingTop +
      paddingBottom +
      (footer?.offsetHeight || 0);

    return `calc(100vh - ${height}px)`;
  };

  if (!dataBlock || !dataTemplate || !permissionChecked) {
    return <BaseSpinner />;
  }

  return (
    <div className="flex flex-col">
      <div className="data-block-header-buttons page-header">
        {location.state?.showBackButton && (
          <BaseButton
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.TERTIARY}
            type={BUTTON_TYPES.LINK}
            to={-1}
          >
            {t("datablocks.labels.back")}
          </BaseButton>
        )}
        {showCtas &&
        currentUser &&
        currentUser.organizationRole !== OrganizationRoles.GUEST.value &&
        dataBlock.editable ? (
          <BaseButton
            size="small"
            to={"/discussions/form"}
            type={BUTTON_TYPES.LINK}
            state={{ dataBlock }}
          >
            {t("datablocks.labels.startDiscussion")}
          </BaseButton>
        ) : null}
      </div>

      <div className="data-block-header page-header">
        <div className="data-block-header-content flex gap-200 items-start">
          <BaseTag
            style={
              dataTemplate.color
                ? {
                    backgroundColor: `${dataTemplate.color}33`,
                    borderColor: dataTemplate.color,
                  }
                : undefined
            }
            label={dataTemplate.title}
          />

          {titleFieldValue &&
            getContentItemForField(titleFieldValue, { label: undefined })}
        </div>

        <div className="data-block-header-content flex gap-200 items-start">
          {showCtas ? (
            <DataBlockActionButtons
              dataBlock={dataBlock}
              dataTemplate={dataTemplate}
              setOpenDeleteModal={setOpenDeleteModal}
              setEditRequestModal={setEditRequestModal}
            />
          ) : null}
        </div>
      </div>

      <div
        className="data-block-details-main-content-container flex gap-400"
        style={{ height: `${calculateTotalHeight()}` }}
      >
        <div className="data-block-details-main-content-left-container flex flex-col gap-400 full-height flex-grow">
          <DataBlockDetails dataBlock={dataBlock} dataTemplate={dataTemplate} />
          <DataBlockContent dataBlock={dataBlock} dataTemplate={dataTemplate} />
        </div>

        <div className="data-block-details-main-content-right-container flex-shrink-0">
          <DataBlockSidebar
            dataBlock={dataBlock}
            dataTemplate={dataTemplate}
            showCtas={showCtas}
          />
        </div>
      </div>

      <CancelConfirmationModal
        title={t("confirm.confirmDelete")}
        content={
          <span className="body-m-regular">
            {t("datablocks.labels.confirmDeleteContent")}{" "}
            <span className="body-m-medium">
              &#34;{titleFieldValue?.value as string | undefined}&#34;
            </span>
            ?
          </span>
        }
        confirmButtonText={t("buttons.delete")}
        open={openDeleteModal}
        onConfirm={handleDelete}
        onCancel={() => setOpenDeleteModal(false)}
      />

      <EditRequestModal
        open={editRequestModal.open}
        setOpen={(value) =>
          setEditRequestModal({ open: value, request: undefined })
        }
        request={editRequestModal.request}
      />
    </div>
  );
};

export default DataBlockContainer;
